import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Eye, EyeOff, Lock, Mail, Loader2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const DEFAULT_LOGO = "https://customer-assets.emergentagent.com/job_03e89fda-1599-48f3-846d-f1d3e818b1fa/artifacts/h0q248dw_Marsol.png";

export default function Login() {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({ email: '', password: '' });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [wakingUp, setWakingUp] = useState(false);
  const [logoUrl, setLogoUrl] = useState(DEFAULT_LOGO);

  useEffect(() => {
    axios.get(`${API}/public/branding`)
      .then(r => { if (r.data.main_logo_url) setLogoUrl(r.data.main_logo_url.startsWith('http') ? r.data.main_logo_url : `${process.env.REACT_APP_BACKEND_URL}${r.data.main_logo_url}`); })
      .catch(() => {});
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setWakingUp(false);

    // Show wake-up hint after 5s (Render cold start)
    const hintTimer = setTimeout(() => setWakingUp(true), 5000);

    const delays = [0, 3000, 8000, 15000, 20000];  // tolerate cold start up to ~46s
    let lastErr;
    try {
      for (let i = 0; i < delays.length; i++) {
        if (delays[i] > 0) await new Promise(r => setTimeout(r, delays[i]));
        try {
          const response = await axios.post(`${API}/auth/login`, formData, { timeout: 30000 });
          localStorage.setItem('token', response.data.access_token);
          localStorage.setItem('user', JSON.stringify(response.data.user));
          clearTimeout(hintTimer);
          window.location.href = '/dashboard';
          return;
        } catch (e) {
          lastErr = e;
          // Don't retry on auth errors (invalid password, etc.)
          if (e.response?.status === 401 || e.response?.status === 400) break;
        }
      }
      clearTimeout(hintTimer);
      setError(lastErr?.response?.data?.detail || 'Giriş zamanı xəta baş verdi. Server yuxuda ola bilər, yenidən cəhd edin.');
    } finally {
      setLoading(false);
      setWakingUp(false);
    }
  };

  return (
    <div className="login-page">
      <div className="login-card animate-fade-in">
        {/* Logo */}
        <div className="flex justify-center mb-8">
          <img 
            src={logoUrl} 
            alt="Marsol Group" 
            className="h-16 object-contain"
            data-testid="login-logo"
          />
        </div>

        {/* Title */}
        <h1 
          className="text-2xl font-bold text-center mb-2"
          style={{ color: '#3D4F6F' }}
          data-testid="login-title"
        >
          İdarəetmə Sistemi
        </h1>
        <p className="text-center text-slate-500 mb-8">
          Hesabınıza daxil olun
        </p>

        {/* Error Message */}
        {error && (
          <div 
            className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-xl mb-6 text-sm"
            data-testid="login-error"
          >
            {error}
          </div>
        )}

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-5">
          {/* Email */}
          <div>
            <label className="block text-sm font-semibold text-slate-600 mb-2">
              Email
            </label>
            <div className="relative">
              <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
              <input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                className="input-field"
                style={{ paddingLeft: '48px' }}
                placeholder="admin@marsol.az"
                required
                data-testid="login-email-input"
              />
            </div>
          </div>

          {/* Password */}
          <div>
            <label className="block text-sm font-semibold text-slate-600 mb-2">
              Şifrə
            </label>
            <div className="relative">
              <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
              <input
                type={showPassword ? 'text' : 'password'}
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                className="input-field"
                style={{ paddingLeft: '48px', paddingRight: '48px' }}
                placeholder="Şifrənizi daxil edin"
                required
                data-testid="login-password-input"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                data-testid="toggle-password-visibility"
              >
                {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
              </button>
            </div>
          </div>

          {/* Buttons */}
          <div className="space-y-3 pt-4">
            <button
              type="submit"
              disabled={loading}
              className="btn-primary w-full flex items-center justify-center gap-2"
              data-testid="login-submit-btn"
            >
              {loading ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                'Daxil ol'
              )}
            </button>
            {wakingUp && (
              <p className="text-xs text-amber-600 text-center animate-pulse" data-testid="wake-up-hint">
                Server oyadılır, bir anda hazır olacaq...
              </p>
            )}
          </div>
        </form>

        {/* Footer */}
        <p className="text-center text-slate-400 text-sm mt-8">
          © 2024 Marsol Group. Bütün hüquqlar qorunur.
        </p>
      </div>
    </div>
  );
}
